import { PermissionAccessLevel, PrivilegeLabel } from '@ardoq/api-types';
import { AccessSpec } from './compileAccessSpecs';

/**
 * This type defines the prefix of the privilege descriptor keys and indicates
 * what type of `resourceId` should be passed to `hasPermission`.
 *
 * Eg. `workspace/create_component` will check the resource permissions on the
 * workspace to check if the user is allowed to create component along with
 * organization level privileges for creating a component.
 */
type PermissionResourceType =
  | 'organization'
  | 'dashboard'
  | 'component'
  | 'references'
  | 'report'
  | 'scenario'
  | 'workspace'
  | 'presentation'
  | 'process'
  | 'traversal';

type ActionKeyFormat = `${PermissionResourceType}/${string}`;

/**
 * User has to be resource admin OR organization admin (global data access
 * privilege) in order to satisfy this access spec.
 */
const DEFAULT_ADMIN_SPECS: AccessSpec[] = [
  { resourcePermission: PermissionAccessLevel.ADMIN },
  { orgPrivilege: PrivilegeLabel.GLOBAL_DATA_ACCESS },
];
const DEFAULT_EDIT_SPECS: AccessSpec[] = [
  { resourcePermission: PermissionAccessLevel.EDIT },
  { orgPrivilege: PrivilegeLabel.GLOBAL_DATA_ACCESS },
];
const DEFAULT_READ_SPECS: AccessSpec[] = [
  { resourcePermission: PermissionAccessLevel.READ },
  { orgPrivilege: PrivilegeLabel.GLOBAL_DATA_ACCESS },
];

/**
 * ============================== READ ME =====================================
 * Privilege descriptor properties define what kind of permission or privilege
 * is required to perform an action and are && together, meaning both are
 * required. Examples:
 * - `{ resourcePermission: 'admin' }` means that the user must have admin
 *   permissions on the resource.
 * - `{ orgPrivilege: 'global_data_access' }` means that the user must have
 *   global data access privilege in the organization.
 * - An array of descriptors means that any of the descriptors are sufficient.
 *   E.g. `[ { resourcePermission: 'edit' }, { orgPrivilege: 'global_data_access' } ]`
 *   means that either having edit permissions on the resource OR having global
 *   data access privilege in the organization is sufficient.
 */
export const ardoqAccessSpecs = {
  'organization/create_workspace': {
    orgPrivilege: PrivilegeLabel.CREATE_WORKSPACE,
  },
  'organization/create_dashboard': {
    orgPrivilege: PrivilegeLabel.CREATE_DASHBOARD,
  },
  'dashboard/admin': DEFAULT_ADMIN_SPECS,
  'dashboard/edit': DEFAULT_EDIT_SPECS,
  'dashboard/read': DEFAULT_READ_SPECS,
  'workspace/admin': DEFAULT_ADMIN_SPECS,
  'workspace/edit': DEFAULT_EDIT_SPECS,
  'workspace/read': DEFAULT_READ_SPECS,
  'workspace/add_field': DEFAULT_ADMIN_SPECS,
  'workspace/edit_fields': DEFAULT_ADMIN_SPECS,
  'workspace/edit_reference_types': DEFAULT_ADMIN_SPECS,
  'workspace/edit_attachments': DEFAULT_ADMIN_SPECS,
  'organization/manage_global_fields': {
    orgPrivilege: PrivilegeLabel.GLOBAL_DATA_ACCESS,
  },
  'organization/manage_metamodel_constraints': {
    orgPrivilege: PrivilegeLabel.GLOBAL_DATA_ACCESS,
  },
  'organization/access_discover': {
    orgPrivilege: PrivilegeLabel.ACCESS_DISCOVER,
  },
  'organization/create_scenario': {
    orgPrivilege: PrivilegeLabel.CREATE_SCENARIO,
  },
  'organization/create_gremlin': {
    orgPrivilege: PrivilegeLabel.CREATE_GREMLIN_QUERIES,
  },
  'report/admin': DEFAULT_ADMIN_SPECS,
  'report/edit': DEFAULT_EDIT_SPECS,
  'report/read': DEFAULT_READ_SPECS,
  'scenario/admin': DEFAULT_ADMIN_SPECS,
  'scenario/edit': DEFAULT_EDIT_SPECS,
  'scenario/read': DEFAULT_READ_SPECS,
  'presentation/edit': DEFAULT_EDIT_SPECS,
  'process/edit': DEFAULT_EDIT_SPECS,
  'traversal/admin': DEFAULT_ADMIN_SPECS,
  'traversal/edit': DEFAULT_EDIT_SPECS,
  'traversal/read': DEFAULT_READ_SPECS,
} satisfies Record<ActionKeyFormat, AccessSpec | AccessSpec[]>;
